import { Icon, Loader, spacing } from '@scality/core-ui';
import type { ComponentProps } from 'react';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { useChat } from '../../hooks/useChat';
import { type ToolApprovalRequest, type ToolTrailRow, ToolTrailStatus } from '../../types/ClientMessage';
import { ThinkingLoader } from './ThinkingLoader';
import { ToolApprovalBanner } from './ToolApprovalBanner';

const Rows = styled.ol`
  display: flex;
  flex-direction: column;
  gap: ${spacing.r8};
  margin: 0;
  padding: 0;
  list-style: none;
  white-space: normal;
`;

const Row = styled.li<{ $muted: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.r8};
  font-family: 'Fira Code', monospace;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${(props) => (props.$muted ? props.theme.textTertiary : props.theme.textSecondary)};
`;

const Glyph = styled.span`
  flex-shrink: 0;
  width: 1rem;
  height: 1.5em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Label = styled.span<{ $struck: boolean }>`
  min-width: 0;
  overflow-wrap: anywhere;
  ${(props) =>
    props.$struck &&
    css`
      text-decoration: line-through;
    `}
`;

const Shimmer = styled.span`
  min-width: 0;
  & > span {
    font-size: inherit;
  }
`;

type Mark = { icon: ComponentProps<typeof Icon>['name']; color: string; label: string };

/** A user-approved tool gets its own mark, so it reads apart from one that needed no approval. */
const markOf = (row: ToolTrailRow): Mark | null => {
  switch (row.status) {
    case ToolTrailStatus.Done:
      if (row.approvedByUser) return { icon: 'Check-circle', color: 'statusHealthy', label: 'Done, approved by you' };
      if (row.isNavigation) return { icon: 'Location', color: 'textSecondary', label: 'Navigated' };
      return { icon: 'Check', color: 'textSecondary', label: 'Done' };
    case ToolTrailStatus.Failed:
      return { icon: 'Exclamation-circle', color: 'statusCritical', label: 'Failed' };
    case ToolTrailStatus.Denied:
      return { icon: 'Times-circle', color: 'textSecondary', label: 'Denied' };
    case ToolTrailStatus.Skipped:
      return { icon: 'Minus', color: 'textTertiary', label: 'Not executed' };
    default:
      return null;
  }
};

const rowText = (row: ToolTrailRow) => {
  if (row.status === ToolTrailStatus.Denied) return `Denied — ${row.label}`;
  if (row.status === ToolTrailStatus.Failed) return `${row.label} — failed`;
  return row.label;
};

const TrailRow: React.FC<{ row: ToolTrailRow }> = ({ row }) => {
  const mark = markOf(row);
  const params = useMemo(() => ({ message: row.label }), [row.label]);
  return (
    <Row $muted={row.status === ToolTrailStatus.Skipped}>
      <Glyph>
        {mark ? (
          <Icon name={mark.icon} color={mark.color} size="sm" ariaLabel={mark.label} title={mark.label} />
        ) : (
          <Loader size="smaller" />
        )}
      </Glyph>
      {row.status === ToolTrailStatus.Running ? (
        <Shimmer>
          <ThinkingLoader params={params} />
        </Shimmer>
      ) : (
        <Label $struck={row.status === ToolTrailStatus.Skipped}>{rowText(row)}</Label>
      )}
    </Row>
  );
};

type ToolActivityProps = {
  rows: ToolTrailRow[];
  approval?: ToolApprovalRequest;
};

/**
 * Every tool Guardian used for a request, as one checklist that fills in as the work
 * progresses. When the next tool needs the user's go-ahead, the approval is asked right
 * where that tool will appear.
 */
export const ToolActivity: React.FC<ToolActivityProps> = ({ rows, approval }) => {
  const { toolAuthorization } = useChat();
  return (
    <Rows aria-label="Guardian's actions">
      {rows.map((row) =>
        row.status === ToolTrailStatus.AwaitingApproval ? (
          approval && (
            <li key={row.id}>
              <ToolApprovalBanner
                approval={approval}
                onApprove={toolAuthorization.approve}
                onDeny={toolAuthorization.reject}
              />
            </li>
          )
        ) : (
          <TrailRow key={row.id} row={row} />
        ),
      )}
    </Rows>
  );
};
